import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const URL_MAP = {
  accountsReceivable: 'accounts-receivable',
  officeEquipment: 'office-equipments',
  machineryEquipment: 'machinery-equipments',
  inventory: 'inventories',
  partnershipInterest: 'partnership-interests',
  customerList: 'customer-lists',
  otherBusinessProperty: 'other-business-properties',
};
const DEFAULT_EXEMPTION_CODE = '26-2-103';

export default function BusinessAssetsModal({
  isOpen,
  onClose,
  type,
  item,
  onSave,
  caseId,
  exemptions,
  scope,
}) {
  const [formState, setFormState] = useState({
    description: '',
    amount: '',
    exemption_code: scope === 'TN' ? DEFAULT_EXEMPTION_CODE : '',
    exemption_amount: ''
  });

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setFormState({
        description: item.description || '',
        amount: item.amount || '',
        exemption_code: item.exemption_code || '',
        exemption_amount: item.exemption_amount || '',
        ...(type === 'partnershipInterest' ? { percent_ownership: item.percent_ownership || '' } : {}),
      });
    } else {
      setFormState({
        description: '',
        amount: '',
        exemption_code: scope === 'TN' ? DEFAULT_EXEMPTION_CODE : '',
        exemption_amount: ''
      });
    }
  }, [isOpen, item, type]);

  const handleChange = e => {
    const { name, value, type: inputType } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]:
        inputType === 'number'
          ? value === '' ? '' : parseFloat(value)
          : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const urlType = URL_MAP[type] || type;
    const method = item ? 'PUT' : 'POST';
    const url = `${process.env.REACT_APP_API_URL}/case/${caseId}/${urlType}/${item ? item.id + '/edit/' : 'add/'}`;
    const body = { ...formState };
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      onSave(await res.json());
      onClose();
    } else {
      alert('Failed to save');
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold mb-4">{
          item ? 'Edit' : 'Add'
        } {
          {
            accountsReceivable: 'Accounts Receivable',
            officeEquipment: 'Office Equipment',
            machineryEquipment: 'Machinery/Equipment',
            inventory: 'Inventory',
            partnershipInterest: 'Partnership Interest',
            customerList: 'Customer List',
            otherBusinessProperty: 'Other Business Property',
          }[type]
        }</h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Description field */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formState.description}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          {/* Percent Ownership for partnershipInterest */}
          {type === 'partnershipInterest' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">Percent Ownership</label>
              <input
                type="number"
                name="percent_ownership"
                value={formState.percent_ownership || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          )}
          {/* Amount and Exemption Amount grouping */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 col-span-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Value</label>
              <input
                type="number"
                name="amount"
                value={formState.amount}
                onChange={handleChange}
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Exemption Amount</label>
              <input
                type="number"
                name="exemption_amount"
                value={formState.exemption_amount}
                onChange={handleChange}
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          {/* Exemption Code */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Exemption Code</label>
            <select
              name="exemption_code"
              value={formState.exemption_code}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select code…</option>
              {exemptions.map(ex => (
                <option key={ex.law} value={ex.law}>{ex.asset_name} — {ex.law}</option>
              ))}
            </select>
          </div>
          {/* Buttons */}
          <div className="flex justify-end space-x-2 pt-4 col-span-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              {item ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

BusinessAssetsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  item: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  caseId: PropTypes.string.isRequired,
  exemptions: PropTypes.array.isRequired,
  scope: PropTypes.string.isRequired,
};